import { createColumnHelper, Row } from '@tanstack/react-table'
import React, { useMemo } from 'react'
import { useDateRange } from '../../../store/zustand/dateRangeStore'
import { useParams } from 'react-router'
import { useGetSiteListCreativeSizes } from '../../../api/dealership/campaigns/reports/useGetSiteListCreativeSizes'
import { AsyncStatsCard, StatsCard, Table } from '@demandlocal/components'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useGetCampaignImpressionSiteList } from '../../../api/dealership/campaigns/reports/useGetCampaignImpressionSiteList'

type SiteList = {
  SITE: string
  AD_SIZES: number
  IMPRESSIONS: number
}
const siteColumnHelper = createColumnHelper<SiteList>()

const SiteListCreativeSizeMetrics = ({ row }: { row: Row<SiteList> }): JSX.Element => {
  const { dateRange } = useDateRange()
  const { campaign_id } = useParams()
  const { data, isLoading } = useGetSiteListCreativeSizes({
    filters: { ...dateRange.dateRange, site: row.original.SITE, campaign_id },
  })

  return (
    <div className={'flex flex-row items-center gap-3 bg-gainsboro-gray/30 p-5 '}>
      {isLoading &&
        Array.from({ length: 4 }, () => 0)?.map((_, index) => {
          return (
            <div key={index} className="border border-2 rounded-xl w-full">
              <AsyncStatsCard title={''} content={<></>} isLoading={true} />
            </div>
          )
        })}
      {data?.map((ad) => {
        return (
          <div key={ad.SIZE} className="border border-2 rounded-xl w-1/4">
            <StatsCard
              title={<div className="font-semibold">Size: {ad.SIZE}</div>}
              content={
                <div className="text-sm font-bold">
                  Imps: {formatNumber(Number(ad.IMPRESSIONS) || 0)}
                </div>
              }
            />
          </div>
        )
      })}
    </div>
  )
}

const SiteList = () => {
  const { dateRange } = useDateRange()
  const { campaign_id } = useParams()
  const siteColumns = useMemo(
    () => [
      siteColumnHelper.accessor('SITE', {
        header: 'Site',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.original.SITE}
            </button>
          ) : (
            row.original.SITE
          )
        },
      }),
      siteColumnHelper.accessor('AD_SIZES', {
        header: 'Ad Sizes',
        cell: (info) => info.getValue(),
      }),
      siteColumnHelper.accessor('IMPRESSIONS', {
        header: 'Impressions',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
      }),
    ],
    [],
  )

  return (
    <Table
      emptyLabel={'No sites found'}
      columns={siteColumns}
      filters={{
        campaign_id: campaign_id,
        ...dateRange.dateRange,
      }}
      fetchFn={useGetCampaignImpressionSiteList}
      defaultSorting={[
        {
          id: 'IMPRESSIONS',
          desc: true,
        },
      ]}
      getRowCanExpand={() => true}
      renderSubComponent={SiteListCreativeSizeMetrics}
    />
  )
}

export default SiteList
